/**
 * Permission code constants — single source of truth.
 * These must match the `permissions.code` values seeded in the database
 * (see `supabase/migrations/20260208000005_seed_system.sql`).
 *
 * Usage: import { P } from '@core/constants/permissions';
 *        this.rbac.hasPermission(P.PRODUCT_CREATE);
 */
export const P = {
  // Admin / Organization
  ORGANIZATION_ADMIN: 'organization:admin',
  MEMBER_VIEW: 'member:view',
  MEMBER_INVITE: 'member:invite',
  MEMBER_MANAGE: 'member:manage',
  MEMBER_DEACTIVATE: 'member:deactivate',
  BRANCH_CREATE: 'branch:create',
  BRANCH_READ: 'branch:read',
  BRANCH_UPDATE: 'branch:update',
  BRANCH_DELETE: 'branch:delete',
  BRANCH_SWITCH: 'branch:switch',
  BRANCH_ACCESS_ALL: 'branch:access:all',
  REPORT_VIEW_OWN: 'report:view:own',
  REPORT_VIEW_BRANCH: 'report:view:branch',
  REPORT_VIEW_ALL: 'report:view:all',
  REPORT_GENERATE: 'report:generate',

  // POS & Orders
  POS_OPERATE: 'pos:operate',
  POS_VOID: 'pos:void',
  POS_REFUND: 'pos:refund',
  POS_DISCOUNT: 'pos:discount',
  ORDER_CREATE: 'order:create',
  ORDER_READ_OWN: 'order:read:own',
  ORDER_READ_BRANCH: 'order:read:branch',
  ORDER_READ_ALL: 'order:read:all',
  ORDER_UPDATE_OWN: 'order:update:own',
  ORDER_UPDATE_BRANCH: 'order:update:branch',
  ORDER_CANCEL_OWN: 'order:cancel:own',
  ORDER_CANCEL_BRANCH: 'order:cancel:branch',
  PAYMENT_READ_OWN: 'payment:read:own',
  PAYMENT_READ_BRANCH: 'payment:read:branch',

  // Catalog
  MENU_MANAGE: 'menu:manage',
  MENU_VIEW: 'menu:view',
  PRODUCT_CREATE: 'product:create',
  PRODUCT_READ: 'product:read',
  PRODUCT_UPDATE: 'product:update',
  PRODUCT_DELETE: 'product:delete',
  CATEGORY_CREATE: 'category:create',
  CATEGORY_READ: 'category:read',
  CATEGORY_UPDATE: 'category:update',
  CATEGORY_DELETE: 'category:delete',
  MODIFIER_CREATE: 'modifier:create',
  MODIFIER_READ: 'modifier:read',
  MODIFIER_UPDATE: 'modifier:update',
  MODIFIER_DELETE: 'modifier:delete',

  // Inventory
  INVENTORY_MANAGE: 'inventory:manage',
  INVENTORY_VIEW_BRANCH: 'inventory:view:branch',
  INVENTORY_VIEW_ALL: 'inventory:view:all',
  INVENTORY_MANAGE_BRANCH: 'inventory:manage:branch',
  INVENTORY_MANAGE_ALL: 'inventory:manage:all',

  // Discounts
  DISCOUNT_CREATE: 'discount:create',
  DISCOUNT_READ: 'discount:read',
  DISCOUNT_UPDATE: 'discount:update',
  DISCOUNT_DELETE: 'discount:delete',

  // Media
  MEDIA_DELETE: 'media:delete',

  // Business (seeded for Owner + Admin — gates organization-settings + feature-flags)
  BUSINESS_UPDATE: 'business:update',

  // Shifts
  SHIFT_OPEN: 'shift:open',
  SHIFT_CLOSE: 'shift:close',
  SHIFT_VIEW: 'shift:view',
  SHIFT_MANAGE: 'shift:manage',

  // Kitchen Stations
  STATION_CREATE: 'station:create',
  STATION_READ: 'station:read',
  STATION_UPDATE: 'station:update',
  STATION_DELETE: 'station:delete',

  // Estimates
  ESTIMATE_CREATE: 'estimate:create',
  ESTIMATE_READ: 'estimate:read',
  ESTIMATE_UPDATE: 'estimate:update',
  ESTIMATE_DELETE: 'estimate:delete',

  // Activity Log
  ACTIVITY_LOG_VIEW: 'activity_log:view',
  ACTIVITY_LOG_VIEW_OWN: 'activity_log:view:own',

  // Reservations
  RESERVATION_VIEW: 'reservation:view',
  RESERVATION_CREATE: 'reservation:create',
  RESERVATION_UPDATE: 'reservation:update',
  RESERVATION_DELETE: 'reservation:delete',

  // Time & Attendance
  ATTENDANCE_PUNCH: 'attendance:punch',
  ATTENDANCE_VIEW: 'attendance:view',
  ATTENDANCE_MANAGE: 'attendance:manage',

  // System
  SYSTEM_ADMIN: 'system:admin',
} as const;

export type PermissionCode = (typeof P)[keyof typeof P];
